import { deepCloneLayer } from '@/editor/history/cloneUtils'
import type { EditorHistoryCommand } from '@/editor/history/types'
import { getMockupHistoryBridge } from '@/editor/history/mockupEditorBridge'
import type { AnimationLayer } from '@/types/animationLayer'

/**
 * 整表图层列表 + 选中态快照（用于添加 / 删除图层等）。
 */
export class AnimationLayersStackCommand implements EditorHistoryCommand {
  readonly label: string

  private readonly beforeLayers: AnimationLayer[]

  private readonly afterLayers: AnimationLayer[]

  private readonly beforeSelectedLayerId: string | null

  private readonly beforeSelectedClipId: string | null

  private readonly afterSelectedLayerId: string | null

  private readonly afterSelectedClipId: string | null

  /**
   * @param beforeLayers - 变更前全部图层
   * @param afterLayers - 变更后全部图层
   * @param beforeSelectedLayerId - 变更前选中图层
   * @param beforeSelectedClipId - 变更前选中 clip
   * @param afterSelectedLayerId - 变更后选中图层
   * @param afterSelectedClipId - 变更后选中 clip
   * @param label - 描述
   */
  constructor(
    beforeLayers: AnimationLayer[],
    afterLayers: AnimationLayer[],
    beforeSelectedLayerId: string | null,
    beforeSelectedClipId: string | null,
    afterSelectedLayerId: string | null,
    afterSelectedClipId: string | null,
    label: string,
  ) {
    this.label = label
    this.beforeLayers = beforeLayers.map(deepCloneLayer)
    this.afterLayers = afterLayers.map(deepCloneLayer)
    this.beforeSelectedLayerId = beforeSelectedLayerId
    this.beforeSelectedClipId = beforeSelectedClipId
    this.afterSelectedLayerId = afterSelectedLayerId
    this.afterSelectedClipId = afterSelectedClipId
  }

  /** @inheritdoc */
  revert(): void {
    this.applyStack(this.beforeLayers, this.beforeSelectedLayerId, this.beforeSelectedClipId)
  }

  /** @inheritdoc */
  redo(): void {
    this.applyStack(this.afterLayers, this.afterSelectedLayerId, this.afterSelectedClipId)
  }

  /**
   * @param layers - 目标列表
   * @param selL - 选中图层 id
   * @param selC - 选中 clip id
   */
  private applyStack(layers: AnimationLayer[], selL: string | null, selC: string | null): void {
    const b = getMockupHistoryBridge()
    const next = layers.map(deepCloneLayer)
    /** 不在此处 revoke blob：撤销「添加图层」后仍需同一 URL 供重做；仅依赖命令 `dispose` 与显式删除路径释放 */
    b.animationLayers.value = next
    b.selectedLayerId.value = selL
    b.selectedClipId.value = selC
    b.syncLayerSelection()
  }

  /** @inheritdoc */
  dispose(reason: 'evicted' | 'cleared'): void {
    void reason
    const b = getMockupHistoryBridge()
    const live = b.animationLayers.value
    const scan = [...this.beforeLayers, ...this.afterLayers]
    const seen = new Set<string>()
    for (const snap of scan) {
      const url = snap.screenTextureSrc
      if (!url || seen.has(url)) continue
      seen.add(url)
      const stillUsed = live.some((l) => l.screenTextureSrc === url)
      if (!stillUsed) {
        try {
          URL.revokeObjectURL(url)
        } catch {
          /* ignore */
        }
      }
    }
  }
}
